import { 
  CallLog, 
  AudioSession, 
  Recording, 
  CallTransfer, 
  PhoneNumber, 
  DtmfEvent,
  User 
} from '../types';

export const generateMockData = () => {
  const callLogs: CallLog[] = [];
  const audioSessions: AudioSession[] = [];
  const callTransfers: CallTransfer[] = [];
  const recordings: Recording[] = [];
  const phoneNumbers: PhoneNumber[] = [];
  const dtmfEvents: DtmfEvent[] = [];
  
  const users: User[] = [
    // Service Provider (CPass) Users - kept for backward compatibility in user management
    {
      id: 1,
      username: 'superadmin',
      firstName: 'Super',
      lastName: 'Admin',
      email: 'super@cpass.com',
      organizations: [
        {
          id: 'org-service-1',
          name: 'CPass Platform',
          type: 'service',
          role: 'super_admin'
        }
      ],
      role: 'super_admin',
      user_type: 'service'
    },
    {
      id: 2,
      username: 'serviceadmin',
      firstName: 'Service',
      lastName: 'Admin',
      email: 'serviceadmin@cpass.com',
      organizations: [
        {
          id: 'org-service-1',
          name: 'CPass Platform',
          type: 'service',
          role: 'service_admin'
        }
      ],
      role: 'service_admin',
      user_type: 'service'
    },
    {
      id: 3,
      username: 'serviceuser',
      firstName: 'Service',
      lastName: 'User',
      email: 'serviceuser@cpass.com',
      organizations: [
        {
          id: 'org-service-1',
          name: 'CPass Platform',
          type: 'service',
          role: 'service_user'
        }
      ],
      role: 'service_user',
      user_type: 'service'
    },

    // Business/Tenant Users
    {
      id: 4,
      username: 'operatoradmin1',
      firstName: 'Acme',
      lastName: 'Administrator',
      email: 'admin@acmecorp.com',
      organizations: [
        {
          id: 'org-biz-001',
          name: 'Acme Corporation',
          type: 'business',
          role: 'operator_admin',
          business_id: 'BIZ-001'
        }
      ],
      role: 'operator_admin',
      business_id: 'BIZ-001',
      user_type: 'business'
    },
    {
      id: 5,
      username: 'operatoruser1',
      firstName: 'Acme',
      lastName: 'Operator',
      email: 'user@acmecorp.com',
      organizations: [
        {
          id: 'org-biz-001',
          name: 'Acme Corporation',
          type: 'business',
          role: 'operator_user',
          business_id: 'BIZ-001'
        }
      ],
      role: 'operator_user',
      business_id: 'BIZ-001',
      user_type: 'business'
    },
    {
      id: 6,
      username: 'operatoradmin2',
      firstName: 'TechStart',
      lastName: 'Administrator',
      email: 'admin@techstart.com',
      organizations: [
        {
          id: 'org-biz-002',
          name: 'TechStart Inc',
          type: 'business',
          role: 'operator_admin',
          business_id: 'BIZ-002'
        }
      ],
      role: 'operator_admin',
      business_id: 'BIZ-002',
      user_type: 'business'
    },
    {
      id: 7,
      username: 'operatoruser2',
      firstName: 'TechStart',
      lastName: 'Operator',
      email: 'user@techstart.com',
      organizations: [
        {
          id: 'org-biz-002',
          name: 'TechStart Inc',
          type: 'business',
          role: 'operator_user',
          business_id: 'BIZ-002'
        }
      ],
      role: 'operator_user',
      business_id: 'BIZ-002',
      user_type: 'business'
    }
  ];
  
  const statuses: Array<'answered' | 'missed' | 'busy' | 'completed'> = ['answered', 'missed', 'busy', 'completed'];
  const callTypes: Array<'IN' | 'OUT'> = ['IN', 'OUT'];
  const languages = ['en', 'hi', 'es'];
  const dtmfContexts: Array<'IVR_MENU' | 'AUTHENTICATION' | 'SELECTION' | 'INPUT'> = ['IVR_MENU', 'AUTHENTICATION', 'SELECTION', 'INPUT'];
  
  // Generate phone numbers
  for (let i = 0; i < 15; i++) {
    phoneNumbers.push({
      id: i + 1,
      dialing_code: `${i + 1}`,
      cli_number: `+91${Math.floor(Math.random() * 9000000000) + 1000000000}`,
      transcribe_name: `IVR Line ${i + 1}`,
      operator_id: `OP-${Math.floor(Math.random() * 5) + 1}`,
      service_id: `SVC-${Math.floor(Math.random() * 10) + 1}`,
      default_language: languages[Math.floor(Math.random() * 3)]
    });
  }
  
  // Generate call logs and related data
  for (let i = 0; i < 150; i++) {
    const startTime = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000);
    const duration = Math.floor(Math.random() * 600);
    const uniqueId = `CALL-${1000 + i}`;
    
    callLogs.push({
      id: i + 1,
      unique_id: uniqueId,
      caller_id: `+91${Math.floor(Math.random() * 9000000000) + 1000000000}`,
      dialed_number: phoneNumbers[Math.floor(Math.random() * phoneNumbers.length)].cli_number,
      call_type: callTypes[Math.floor(Math.random() * 2)],
      call_status: statuses[Math.floor(Math.random() * 4)],
      start_time: startTime,
      end_time: new Date(startTime.getTime() + duration * 1000),
      duration: duration,
      business_id: `BIZ-${Math.floor(Math.random() * 100)}`,
      agent_id: `AGENT-${Math.floor(Math.random() * 10)}`,
      operator_id: `OP-${Math.floor(Math.random() * 5)}`,
      transferred_to: Math.random() > 0.8 ? `+91${Math.floor(Math.random() * 9000000000) + 1000000000}` : null,
      recording_url:''
    });
    
    // Generate audio sessions
    if (i < 120) {
      const sessionRef = `SES-${1000 + i}`;
      const hasTransfer = !!callLogs[i].transferred_to;
      const transferType = hasTransfer ? (Math.random() > 0.5 ? 'ai' : 'hotkey') : null;
      const terminationSources: Array<'user' | 'ai' | 'system' | 'timeout'> = ['user', 'ai', 'system', 'timeout'];
      const terminationSource = terminationSources[Math.floor(Math.random() * terminationSources.length)];

      audioSessions.push({
        id: i + 1,
        call_log_id: i + 1,
        session_ref: sessionRef,
        audio_duration_ms: duration * 1000,
        transcription_events: Math.floor(Math.random() * 20),
        dtmf_events: Math.floor(Math.random() * 5),
        connection_start: startTime,
        connection_end: new Date(startTime.getTime() + duration * 1000),
        call_summary: `Call summary for session ${sessionRef}: Customer inquired about service pricing and availability. Agent provided detailed information and scheduled a follow-up call.`,
        transfer_call_summary: hasTransfer && Math.random() > 0.7
          ? `Transfer summary: Call was transferred to ${callLogs[i].transferred_to}. Customer requested specialized support. Transfer completed successfully.`
          : '',
        transcription: `Agent: Hello, thank you for calling. How may I help you today?\nCustomer: Hi, I wanted to inquire about your services.\nAgent: I'd be happy to help you with that...`,
        // Transfer information
        transfer_type: transferType,
        transferred_to: callLogs[i].transferred_to,
        transfer_status: hasTransfer ? (Math.random() > 0.3 ? 'ANSWER' : 'NOANSWER') : null,
        // Call information
        caller_id: callLogs[i].caller_id,
        dialed_number: callLogs[i].dialed_number,
        call_type: callLogs[i].call_type,
        call_status: callLogs[i].call_status,
        call_id: uniqueId,
        // Termination information
        termination_source: terminationSource,
        // DTMF collection - will be populated below
        dtmf_collection: []
      });
      
      // Generate recordings
      recordings.push({
        id: i + 1,
        session_id: i + 1,
        call_log_id: i + 1,
        recording_url: `https://example.com/recordings/${sessionRef}.mp3`,
        file_size: Math.floor(Math.random() * 5000000) + 500000,
        duration_ms: duration * 1000,
        created_at: startTime
      });
      
      // Generate DTMF events and add to audio session
      if (Math.random() > 0.5) {
        const numDtmf = Math.floor(Math.random() * 8) + 1;
        const sessionDtmfEvents: DtmfEvent[] = [];
        for (let j = 0; j < numDtmf; j++) {
          const dtmfEvent: DtmfEvent = {
            id: dtmfEvents.length + 1,
            call_log_id: i + 1,
            digit: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'][Math.floor(Math.random() * 12)],
            timestamp: new Date(startTime.getTime() + Math.random() * duration * 1000),
            context: dtmfContexts[Math.floor(Math.random() * 4)]
          };
          dtmfEvents.push(dtmfEvent);
          sessionDtmfEvents.push(dtmfEvent);
        }
        // Add DTMF events to the audio session
        audioSessions[audioSessions.length - 1].dtmf_collection = sessionDtmfEvents;
      }
    }
    
    // Generate call transfers
    if (callLogs[i].transferred_to && Math.random() > 0.5) {
      const transferTime = startTime.getTime() + Math.random() * duration * 1000;
      const isCompleted = Math.random() > 0.3;
      const transferType = Math.random() > 0.5 ? 'ai' : 'hotkey';

      callTransfers.push({
        id: callTransfers.length + 1,
        businessId: callLogs[i].business_id,
        originalCallId: uniqueId,
        newCallId: `CALL-TRANS-${2000 + callTransfers.length}`,
        callerId: callLogs[i].caller_id,
        transferredTo: callLogs[i].transferred_to!,
        transferredFrom: callLogs[i].dialed_number,
        transferType: transferType as 'ai' | 'hotkey',
        transferCallSummary: transferType === 'ai'
          ? `This call was transferred by AI. Customer requested specialized support for ${['billing', 'technical', 'sales'][Math.floor(Math.random() * 3)]} assistance.`
          : `This call transferred due to user pressing ${['0', '1', '2'][Math.floor(Math.random() * 3)]} as hotkey.`,
        transferStatus: isCompleted ? 'ANSWER' : 'NOANSWER',
        transferTime: transferTime,
        completedTime: isCompleted ? transferTime + Math.random() * 60000 : null,
        createdAt: transferTime
      });
    }
  }
  
  return { 
    callLogs, 
    audioSessions, 
    callTransfers, 
    recordings, 
    phoneNumbers, 
    users, 
    dtmfEvents 
  };
};